import { validationPatterns } from "@ui/utils/validationPatterns";

import type { PasswordRequirementsResponse } from "@jti/fe-common";

const { CONTAINS_LOWER_CASE, CONTAINS_NUMBER, CONTAINS_UPPER_CASE, SPECIAL_CHARACTER } = validationPatterns;

export const MAX_PASSWORD_LENGTH = 50;

export const PASSWORD_REQUIREMENT_KEYS = [
  "passwordMinLength",
  "passwordMaxLength",
  "containNumber",
  "containSpecial",
  "containLettersLowerCase",
  "containLettersUpperCase",
  "excludeUsername",
] as const;

export type PasswordRequirementKey = (typeof PASSWORD_REQUIREMENT_KEYS)[number];

export type PasswordValidationResult = Record<PasswordRequirementKey, boolean>;

const countMatches = (value: string, pattern: RegExp) => (value.match(pattern) || []).length;

export const isPasswordDifferentFromEmail = (passwordValue: string, email?: string) => {
  const [addressPart, domainPart] = email?.toLowerCase().split("@") || [];

  if (addressPart && domainPart) {
    const addressParts = addressPart.split(".");
    const domainParts = domainPart.split(".");

    if (addressParts.length && domainParts.length) {
      return ![...addressParts, domainParts[0]].some((part) => passwordValue.toLowerCase().includes(part));
    }
  }

  return true;
};

export const validatePassword = (
  passwordValue: string,
  passwordRules: PasswordRequirementsResponse,
  email?: string
): PasswordValidationResult => ({
  passwordMinLength: passwordValue.length >= passwordRules.minLength,
  passwordMaxLength: passwordValue.length > 0 && passwordValue.length <= MAX_PASSWORD_LENGTH,
  containNumber: countMatches(passwordValue, CONTAINS_NUMBER) >= passwordRules.minNumber,
  containSpecial: countMatches(passwordValue, SPECIAL_CHARACTER) >= passwordRules.minSymbol,
  containLettersLowerCase: countMatches(passwordValue, CONTAINS_LOWER_CASE) >= passwordRules.minLowerCase,
  containLettersUpperCase: countMatches(passwordValue, CONTAINS_UPPER_CASE) >= passwordRules.minUpperCase,
  excludeUsername:
    !passwordRules.excludeUsername || (isPasswordDifferentFromEmail(passwordValue, email) && passwordValue.length > 0),
});

export const hasUnmetRequirements = (validationResult: PasswordValidationResult) =>
  Object.values(validationResult).some((isMet) => !isMet);

export const isRequirementEnabled = (key: PasswordRequirementKey, passwordRules: PasswordRequirementsResponse) => {
  switch (key) {
    case "passwordMinLength":
      return passwordRules.minLength > 0;
    case "passwordMaxLength":
      return true;
    case "containNumber":
      return passwordRules.minNumber > 0;
    case "containSpecial":
      return passwordRules.minSymbol > 0;
    case "containLettersLowerCase":
      return passwordRules.minLowerCase > 0;
    case "containLettersUpperCase":
      return passwordRules.minUpperCase > 0;
    case "excludeUsername":
      return passwordRules.excludeUsername;
    default:
      return false;
  }
};
